import type { ApprovedListLine } from '@felix/domain';
import { useTranslation } from 'react-i18next';
import { StatusBadge } from '../../StatusBadge.js';
import table from '../materials/Materials.module.css';
import classes from './Purchasing.module.css';
import { formatQuantity } from './requisitions.js';
import type { RequisitionDetail } from './types.js';

/**
 * Onaylı liste (ADR 0111 §A; sahip 2026-10-23: "ona onaylanan liste gelir ve gemi bu listeye göre
 * hareket eder"): ofisin son revizyonu. Değişen miktar `warning` tonunda "100 → 150", ofisin
 * eklediği kalem `info` rozetli, kaldırılan kalem üstü çizili ve "Removed" rozetli; gerekçe ve
 * revizyon başlıkta. Gemide ve ofiste aynı görünüm.
 */
export function ApprovedListTable({
  detail,
  stamp,
}: {
  detail: RequisitionDetail;
  stamp: (at: string | null) => string;
}) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? 'en';
  const k = (key: string, options: Record<string, unknown> = {}) =>
    t(`purchasing.review.${key}`, options);
  const q = (value: number) => formatQuantity(value, locale);
  const lines = detail.approvedList ?? [];
  const revision = detail.revision ?? null;
  return (
    <>
      {revision ? (
        <section className={classes.notice} data-tone="warning" aria-label={k('approvedTab')}>
          <p className={classes.reviewMeta}>
            <strong>
              {k('revisionLine', { no: revision.revisionNo, at: stamp(revision.createdAt) })}
            </strong>
            {revision.byName ? <span>{k('revisionBy', { name: revision.byName })}</span> : null}
          </p>
          <p className={classes.noteLine}>{revision.reason}</p>
          <p className={classes.noteLine}>{k('legend')}</p>
        </section>
      ) : (
        <p className={classes.notice} data-tone="info" role="status">
          {k('noRevision')}
        </p>
      )}
      <section
        className={table.tableWrap}
        aria-label={k('table')}
        // biome-ignore lint/a11y/noNoninteractiveTabindex: kaydırılabilir bölge (WCAG 2.1.1)
        tabIndex={0}
      >
        <table className={`${table.table} ${classes.table}`}>
          <thead>
            <tr>
              <th scope="col" className={classes.index}>
                {k('columns.no')}
              </th>
              <th scope="col" className={classes.key}>
                {k('columns.item')}
              </th>
              <th scope="col" data-numeric>
                {k('columns.requested')}
              </th>
              <th scope="col" data-numeric>
                {k('columns.approved')}
              </th>
              <th scope="col">{k('columns.uom')}</th>
              <th scope="col">{k('columns.note')}</th>
            </tr>
          </thead>
          <tbody>
            {lines.map((line) => (
              <ApprovedRow key={line.key} line={line} q={q} k={k} />
            ))}
          </tbody>
        </table>
      </section>
    </>
  );
}

function ApprovedRow({
  line,
  q,
  k,
}: {
  line: ApprovedListLine;
  q: (value: number) => string;
  k: (key: string, options?: Record<string, unknown>) => string;
}) {
  const changed = line.change === 'increased' || line.change === 'decreased';
  return (
    <tr data-change={line.change}>
      <td className={classes.index}>{line.lineNo ?? '—'}</td>
      <td className={`${classes.material} ${classes.key}`}>
        {line.name}
        {line.change === 'added' ? (
          <span className={classes.stockBadge} data-keep>
            {' '}
            <StatusBadge tone="info">{k('added')}</StatusBadge>
          </span>
        ) : null}
        {line.change === 'removed' ? (
          <span className={classes.stockBadge}>
            {' '}
            <StatusBadge tone="neutral">{k('removed')}</StatusBadge>
          </span>
        ) : null}
      </td>
      <td data-numeric>{line.requested === null ? '—' : q(line.requested)}</td>
      <td
        data-numeric
        data-tone={changed ? 'warning' : line.change === 'added' ? 'info' : undefined}
      >
        {changed && line.requested !== null
          ? k('quantityChange', { from: q(line.requested), to: q(line.approved) })
          : line.change === 'removed'
            ? q(0)
            : q(line.approved)}
      </td>
      <td>{line.unit}</td>
      <td className={classes.muted} data-keep>
        {line.note ?? '—'}
      </td>
    </tr>
  );
}
